import { useCallback, useState } from 'react';
import { pb } from '@/core/api';
import { useAuthStore } from '@/stores/auth';
import type { ProfileUpdate } from '../types';

const ERR_SAVE = 'Error al guardar. Intentalo de nuevo.';

type State = {
  values: ProfileUpdate;
  initial: ProfileUpdate;
  error: string;
  saving: boolean;
  saved: boolean;
};

function diff(values: ProfileUpdate, initial: ProfileUpdate): boolean {
  const ks = Object.keys(values) as (keyof ProfileUpdate)[];
  for (const k of ks) {
    if ((values[k] ?? '') !== (initial[k] ?? '')) return true;
  }
  return false;
}

type SetS = (updater: (p: State) => State) => void;

async function doSubmit(
  values: ProfileUpdate,
  setS: SetS,
  refresh: () => Promise<unknown>
): Promise<void> {
  setS((p) => ({ ...p, saving: true, error: '' }));
  try {
    await pb.send('/api/team/me/profile', { method: 'POST', body: values });
    setS((p) => ({ ...p, saving: false, saved: true, initial: p.values }));
    await refresh();
  } catch {
    setS((p) => ({ ...p, saving: false, error: ERR_SAVE }));
  }
}

const FRESH = { error: '', saving: false, saved: false } as const;

const init = (v: ProfileUpdate): State => ({ values: v, initial: v, ...FRESH });

const fieldUpdater =
  <K extends keyof ProfileUpdate>(k: K, v: ProfileUpdate[K]) =>
  (p: State): State => ({
    ...p,
    values: { ...p.values, [k]: v },
    error: '',
    saved: false,
  });

const resetUpdater = (p: State): State => ({
  ...p,
  values: p.initial,
  error: '',
  saved: false,
});

export function useSelfProfile(initial: ProfileUpdate) {
  const refresh = useAuthStore((s) => s.refreshAuth);
  const [s, setS] = useState<State>(() => init(initial));
  const setField = useCallback(
    <K extends keyof ProfileUpdate>(k: K, v: ProfileUpdate[K]): void =>
      setS(fieldUpdater(k, v)),
    []
  );
  const submit = useCallback(
    () => doSubmit(s.values, setS, refresh),
    [s.values, refresh]
  );
  const reset = useCallback(() => setS(resetUpdater), []);
  return { ...s, dirty: diff(s.values, s.initial), setField, submit, reset };
}
